import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { jobApiService, Job } from '../../recruiter/services/jobApi';
import { recruiterApplicationApiService, Application, UpdateStageRequest } from '../services/applicationApi';

/**
 * Received Applications Page
 * Displays all applications received for recruiter's jobs
 * Allows stage management and messaging
 */
const ReceivedApplications: React.FC = () => {
  // Auth is handled by JWT tokens in API calls - no need for candidate-specific auth protection

  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications, setApplications] = useState<{ [jobId: string]: Application[] }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [updatingStage, setUpdatingStage] = useState<string | null>(null);

  // Fetch jobs and their applications
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Get recruiter ID from localStorage
        const userDataString = localStorage.getItem('authUser');
        let recruiterId = '';

        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            recruiterId = userData.recruiterId || userData.id || '';
            console.log('Fetching jobs for recruiter ID:', recruiterId);
          } catch (error) {
            console.error('Failed to parse user data:', error);
          }
        }

        // Fetch all jobs and filter by recruiterId or postedBy
        const { jobs: allJobs } = await jobApiService.getAllJobs();
        const fetchedJobs = allJobs.filter(job =>
          job.recruiterId === recruiterId || job.postedBy === recruiterId
        );

        console.log(`Found ${fetchedJobs.length} jobs for recruiter (out of ${allJobs.length} total)`);
        setJobs(fetchedJobs);

        // Fetch applications for each job
        const applicationsMap: { [jobId: string]: Application[] } = {};
        for (const job of fetchedJobs) {
          const jobId = job.id || job._id;
          if (!jobId) continue;
          try {
            const jobApplications = await recruiterApplicationApiService.getJobApplications(jobId);
            applicationsMap[jobId] = jobApplications;
          } catch (error) {
            console.error(`Failed to fetch applications for job ${jobId}:`, error);
            applicationsMap[jobId] = [];
          }
        }
        setApplications(applicationsMap);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStageChange = async (applicationId: string, jobId: string, newStage: Application['currentStage']) => {
    try {
      setUpdatingStage(applicationId);

      const request: UpdateStageRequest = {
        stage: newStage,
        notes: `Stage updated to ${newStage}`
      };

      const updatedApplication = await recruiterApplicationApiService.updateApplicationStage(applicationId, request);

      // Update local state
      setApplications(prev => ({
        ...prev,
        [jobId]: prev[jobId].map(app =>
          app.id === applicationId ? updatedApplication : app
        )
      }));

      alert(`Application stage updated to ${newStage}`);
    } catch (error: any) {
      console.error('Error updating stage:', error);
      alert(error.message || 'Failed to update application stage');
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleOpenMessages = (application: Application) => {
    // Navigate to messages with application context
    navigate(`/companies/messages?application=${application.id}&candidate=${application.candidateId}`);
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'interview':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total applications count
  const totalApplications = Object.values(applications).reduce((sum, apps) => sum + (apps?.length || 0), 0);

  return (
    <CompanyLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0B111E]">Received Applications</h1>
        <p className="text-base font-normal text-[#515151] mt-1">
          Manage and review applications for your job postings ({totalApplications} total)
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
            <p className="font-medium text-sm text-[#757575]">Loading applications...</p>
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-[#A5A5A5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-lg font-medium text-[#0B111E]">No jobs posted yet</p>
            <p className="text-sm text-[#757575] mt-1">Post a job to start receiving applications</p>
            <button
              onClick={() => navigate('/companies/job-posting')}
              className="mt-4 px-6 py-2 bg-[#015FA6] text-white rounded-lg font-medium text-sm hover:bg-[#014580] transition-colors"
            >
              Post a Job
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {jobs.map((job) => {
            const jobId = job.id || job._id;
            if (!jobId) return null;
            const jobApplications = applications[jobId] || [];

            return (
              <div key={jobId} className="bg-white rounded-2xl p-6">
                <div className="mb-4 pb-4 border-b border-[#EDEDED]">
                  <h2 className="text-xl font-semibold text-[#0B111E]">{job.title}</h2>
                  <p className="text-sm text-[#757575] mt-1">
                    {job.companyName} • {jobApplications.length} application{jobApplications.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {jobApplications.length === 0 ? (
                  <p className="text-center text-[#757575] py-8">No applications received yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {jobApplications.map((application: Application) => {
                      const isUpdating = updatingStage === application.id;

                      return (
                        <div
                          key={application.id}
                          className="border border-[#EDEDED] rounded-lg p-4"
                        >
                          {/* Candidate Info */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-[#0B111E]">
                                {application.candidateName || 'Candidate'}
                              </h3>
                              <p className="text-sm text-[#757575]">{application.candidateEmail}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStageColor(application.currentStage)}`}>
                              {application.currentStage}
                            </span>
                          </div>

                          {/* Applied Date */}
                          <p className="text-xs text-[#757575] mb-3">
                            Applied: {formatDate(application.appliedAt)}
                          </p>

                          {/* Stage Selector */}
                          <div className="mb-3">
                            <label className="text-xs font-medium text-[#757575] block mb-1">
                              Change Stage:
                            </label>
                            <select
                              value={application.currentStage}
                              onChange={(e) => jobId && handleStageChange(application.id, jobId, e.target.value as Application['currentStage'])}
                              disabled={isUpdating}
                              className="w-full px-3 py-2 border border-[#EDEDED] rounded-lg text-sm focus:outline-none focus:border-[#015FA6] disabled:bg-gray-100"
                            >
                              <option value="applied">Applied</option>
                              <option value="interview">Interview</option>
                              <option value="offered">Offer</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            {(application.currentStage === 'interview' || application.currentStage === 'offered') && (
                              <button
                                onClick={() => handleOpenMessages(application)}
                                className="flex-1 py-2 px-3 bg-[#015FA6] text-white rounded-lg text-sm font-medium hover:bg-[#014580] transition-colors flex items-center justify-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                Message
                              </button>
                            )}
                            <button
                              onClick={() => {
                                // TODO: View application details
                                console.log('View application:', application.id);
                              }}
                              className="flex-1 py-2 px-3 border border-[#015FA6] text-[#015FA6] rounded-lg text-sm font-medium hover:bg-[#015FA6] hover:text-white transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CompanyLayout>
  );
};

export default ReceivedApplications;
